import { Alert, Card, Input, Modal, Radio, Select, Typography } from "antd"
import VSpace from "../../common/VSpace"
import RadioButton from "../../common/RadioButton"
import { useState } from "react"
import { ChargebackStatusChange, NewChargebackStatus } from "../../../domain/invoicing/rainforestApi"
import { useSimulateChargebackStatusChangeMutation, useSimulateDepositStatusChangeMutation, useSimulateNewAchReturnMutation, useSimulateNewChargebackMutation, useSimulateNewDepositMutation } from "../../../domain/invoicing/rainforestQueries"

type Props = {
  open: boolean
  onClose: () => void
}

type SandboxEvent = 'create-chargeback' | 'create-ach-return' | 'update-chargeback-status' | 'create-deposit' | 'update-deposit-status'

const createChargebackOptions = [
  { value: 'InquiryActionRequired', label: 'Inquiry Action Required' },
  { value: 'DisputeActionRequired', label: 'Dispute Action Required' },
]

const updateChargebackStatusOptions = [
  { value: 'DisputeActionRequired', label: 'Dispute Action Required' },
  { value: 'ProvisionalWin', label: 'Provisional Win' },
  { value: 'Won', label: 'Won' },
  { value: 'Lost', label: 'Lost' },
]

const SimulateSandboxEventModal = ({open, onClose}: Props) => {
  const [selectedEvent, setSelectedEvent] = useState<SandboxEvent>('create-chargeback')
  const [payinId, setPayinId] = useState<string>('')
  const [newChargebackStatus, setNewChargebackStatus] = useState<NewChargebackStatus>('InquiryActionRequired')
  const [chargebackId, setChargebackId] = useState<string>('')
  const [chargebackStatusChange, setChargebackStatusChange] = useState<ChargebackStatusChange>('DisputeActionRequired')
  const [depositId, setDepositId] = useState<string>('')
  const [achReturnPayinId, setAchReturnPayinId] = useState<string>('')
  const simulateNewChargebackMutation = useSimulateNewChargebackMutation()
  const simulateNewAchReturnMutation = useSimulateNewAchReturnMutation()
  const simulateChargebackStatusChangeMutation = useSimulateChargebackStatusChangeMutation()
  const simulateNewDepositMutation = useSimulateNewDepositMutation(() => onClose())
  const simulateDepositStatusChangeMutation = useSimulateDepositStatusChangeMutation()

  const handleSimulate = () => {
    if (selectedEvent === 'create-chargeback') {
      simulateNewChargebackMutation.mutate({ payinId, status: newChargebackStatus }, {onSuccess: () => onClose()})
      return
    }
    if (selectedEvent === 'update-chargeback-status') {
      simulateChargebackStatusChangeMutation.mutate({ chargebackId, status: chargebackStatusChange }, {onSuccess: () => onClose()})
      return
    }
    if (selectedEvent === 'create-ach-return') {
      simulateNewAchReturnMutation.mutate({ payinId: achReturnPayinId }, {onSuccess: () => onClose()})
      return
    }
    if (selectedEvent === 'create-deposit') {
      simulateNewDepositMutation.mutate()
      return
    }
    if (selectedEvent === 'update-deposit-status') {
      simulateDepositStatusChangeMutation.mutate({ depositId }, {onSuccess: () => onClose()})
      return
    }
  }

  const isSimulating = simulateNewChargebackMutation.isLoading ||
    simulateNewAchReturnMutation.isLoading ||
    simulateChargebackStatusChangeMutation.isLoading ||
    simulateNewDepositMutation.isLoading ||
    simulateDepositStatusChangeMutation.isLoading

  return <Modal
    open={open}
    onCancel={onClose}
    title="Simulate Sandbox Event"
    okText="Simulate"
    cancelText="Cancel"
    width={600}
    okButtonProps={{ loading: isSimulating, disabled: isSimulating }}
    cancelButtonProps={{ disabled: isSimulating }}
    onOk={handleSimulate}
  >
    <VSpace>
      <Typography.Text type="secondary">Because this tenant is using SynkedUP Payments in the sandbox, you can simulate an event to test the payments system.</Typography.Text>
      <Card title="Select Event">
      <Radio.Group value={selectedEvent} onChange={x => setSelectedEvent(x.target.value)} style={{ width: '100%' }}>
        <VSpace>
          <RadioButton
            value="create-chargeback"
            checked={selectedEvent === 'create-chargeback'}
            onBorderClick={() => setSelectedEvent('create-chargeback')}
          >Create Chargeback</RadioButton>
          <RadioButton
            value="update-chargeback-status"
            checked={selectedEvent === 'update-chargeback-status'}
            onBorderClick={() => setSelectedEvent('update-chargeback-status')}
          >Update Chargeback Status</RadioButton>
          <RadioButton
            value="create-ach-return"
            checked={selectedEvent === 'create-ach-return'}
            onBorderClick={() => setSelectedEvent('create-ach-return')}
          >Create ACH Return</RadioButton>
          <RadioButton
            value="create-deposit"
            checked={selectedEvent === 'create-deposit'}
            onBorderClick={() => setSelectedEvent('create-deposit')}
          >Create Deposit</RadioButton>
          <RadioButton
            value="update-deposit-status"
            checked={selectedEvent === 'update-deposit-status'}
            onBorderClick={() => setSelectedEvent('update-deposit-status')}
          >Update Deposit Status</RadioButton>
        </VSpace>
      </Radio.Group>
      </Card>
      { selectedEvent === 'create-chargeback' && <Card title="Create Chargeback">
        <VSpace>
          Payin ID:
          <Input
            placeholder="sbx_pyi_"
            value={payinId}
            onChange={x => setPayinId(x.target.value)}
          />
          Status:
          <Select
            options={createChargebackOptions}
            value={newChargebackStatus}
            onChange={x => setNewChargebackStatus(x)}
          />
        </VSpace>
      </Card>}
      { selectedEvent === 'update-chargeback-status' && <Card title="Update Chargeback Status">
        <VSpace>
          Chargeback ID:
          <Input
            placeholder="sbx_chb_"
            value={chargebackId}
            onChange={x => setChargebackId(x.target.value)}
          />
          Status:
          <Select
            options={updateChargebackStatusOptions}
            value={chargebackStatusChange}
            onChange={x => setChargebackStatusChange(x)}
          />
          <Alert type="info" message={<p>
            This can fail if the status transition is not allowed by the Rainforest API.
            <br/>
            <br/>
            For example, you can only move to "Provisional Win" from "Chargeback Processing".
            Nor can you move to "Won" from "Dispute Action Required".
            Once the chargeback is "Won" or "Lost", you cannot change the status again.
          </p>} />
        </VSpace>
      </Card>}
      { selectedEvent === 'create-ach-return' && <Card title="Create ACH Return">
        <VSpace>
          Payin ID:
          <Input
            placeholder="sbx_pyi_"
            value={achReturnPayinId}
            onChange={x => setAchReturnPayinId(x.target.value)}
          />
        </VSpace>
      </Card>}
      { selectedEvent === 'update-deposit-status' && <Card title="Update Deposit Status">
        <VSpace>
          Deposit ID:
          <Input
            placeholder="sbx_dep_"
            value={depositId}
            onChange={x => setDepositId(x.target.value)}
          />
        </VSpace>
      </Card>}
    </VSpace>
  </Modal>
}

export default SimulateSandboxEventModal